import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { brandFontStack, defaultBrandKit, isBrandConfigured, type BrandKit } from "@veyso/core";
import { BrandKitPanel } from "../settings/BrandKit.js";
import { VeysoMark } from "../components/VeysoMark.js";
import { api } from "../api.js";
import { capture } from "../lib/posthog.js";
import { ErrorNote } from "../components/ui.js";
import { useInvite } from "../team/useInvite.js";

/**
 * The first thing a new account sees.
 *
 * Every video a workspace makes starts from the brand kit, so an account that
 * has not set one makes its first video in our defaults and then has to go and
 * find out why it does not look like theirs. Asking once, at the start, is the
 * only moment where it is a setup step rather than an interruption.
 *
 * Three steps rather than one page. The first draft put the address field above
 * the whole kit, which meant somebody arriving met a form of a dozen controls
 * and had to work out that the one at the top would fill in the rest. Asking
 * one question, then showing what came back, then opening the panel makes each
 * screen answerable on its own.
 */
type Step = "site" | "confirm" | "kit" | "team";

const SKIP_KEY = "veyso.welcome.skipped";

/**
 * Skipping is remembered for the session, not for ever.
 *
 * For ever would mean somebody who skipped on their first afternoon never being
 * asked again, and a workspace that quietly makes every video in our colours.
 * Not at all would mean a redirect they cannot get out of. A session is the
 * middle: out of the way now, asked again tomorrow.
 */
function skipped(): boolean {
  try {
    return window.sessionStorage.getItem(SKIP_KEY) === "1";
  } catch {
    return false;
  }
}

export function markWelcomeSkipped(): void {
  try {
    window.sessionStorage.setItem(SKIP_KEY, "1");
  } catch {
    // A browser refusing session storage asks again on the next navigation,
    // which is worse than remembering but better than failing to render.
  }
}

/** Whether a fresh account should be sent here. Read by the dashboard. */
export function needsWelcome(kit: BrandKit): boolean {
  return !isBrandConfigured(kit) && !skipped();
}

interface Found {
  colours: number;
  declaredColour: boolean;
  fonts: number;
  logo: boolean;
}

/** What was read, as a sentence. A bare list starting mid-phrase reads as a fragment. */
function readSummary(found: Found): string {
  const parts = [
    found.declaredColour ? "the colour it declares" : null,
    found.colours > 0 ? `${found.colours} colours in its stylesheet` : null,
    found.fonts > 0 ? `${found.fonts} typefaces` : null,
    found.logo ? "a logo" : null,
  ].filter((part): part is string => part !== null);

  if (parts.length === 0) return "We could not read much from that page.";
  const listed = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `We read ${listed}.`;
}

const Swatch: React.FC<{ label: string; colour: string }> = ({ label, colour }) => (
  <div className="flex min-w-0 items-center gap-2.5">
    <span
      className="h-8 w-8 shrink-0 rounded-[6px] border border-[var(--edge)]"
      style={{ background: colour }}
      aria-hidden="true"
    />
    <span className="flex min-w-0 flex-col">
      <span className="text-[11px] text-ink-400">{label}</span>
      <span className="tnum truncate text-[12px] font-medium text-ink-200">{colour.toUpperCase()}</span>
    </span>
  </div>
);

/** The page furniture every step shares, so the three read as one thing. */
const Frame: React.FC<{
  step: Step;
  title: string;
  lede: string;
  children: React.ReactNode;
  /** Ways on through the flow. Right-aligned, primary last. */
  actions: React.ReactNode;
  /** The way out of it entirely. Opposite end, because it is not the same kind of thing. */
  leave: React.ReactNode;
}> = ({ step, title, lede, children, actions, leave }) => {
  const order: Step[] = ["site", "confirm", "kit", "team"];
  const at = order.indexOf(step);

  return (
    <div className="min-h-screen bg-page">
      <div className="mx-auto w-full max-w-[740px] px-6 py-12">
        <div className="flex items-center gap-2.5 text-[13px] font-semibold tracking-tight text-ink-100">
          <VeysoMark />
          Veyso
        </div>

        {/* Three marks rather than "Step 2 of 3". The count is the same
            information and the shape says it without being read. */}
        <div className="mt-8 flex items-center gap-1.5" aria-hidden="true">
          {order.map((name, index) => (
            <span key={name} className={`h-[3px] w-7 rounded-full ${index <= at ? "bg-ember-500" : "bg-bay-750"}`} />
          ))}
        </div>

        <h1 className="mt-5 text-[26px] font-semibold tracking-[-0.02em] text-ink-100">{title}</h1>
        <p className="mt-2 max-w-[52ch] text-[13px] leading-[1.6] text-ink-300">{lede}</p>

        <div className="mt-7">{children}</div>

        {/* Leaving on one side, going on with it on the other. Ranged together
            they wrapped — four controls do not fit this measure — and more to
            the point they read as four equal choices, when one of them is not
            a choice about the brand kit at all. */}
        <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
          <div>{leave}</div>
          <div className="flex flex-wrap items-center gap-3">{actions}</div>
        </div>
      </div>
    </div>
  );
};

/**
 * The last question before the first project: who else works here.
 *
 * Asked now rather than left to settings because a team is worth most before
 * there is anything in it. Invite somebody today and they see every project
 * from the first one; invite them in a month and they arrive to a library they
 * had no part in.
 *
 * One field and a skip. It is the fourth screen of an onboarding somebody
 * wants to be finished with, so it asks once, takes an answer or a shrug, and
 * gets out of the way — the settings panel is where a team is actually managed.
 */
const TeamStep: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const { invite, busy, error, sent, team } = useInvite();
  const [email, setEmail] = useState("");

  // No team means PropelAuth could not make one, which is logged on the server
  // and is not somebody's problem to solve on their fourth screen.
  useEffect(() => {
    if (team === null) onDone();
  }, [team, onDone]);

  return (
    <Frame
      step="team"
      title="Anyone else working on this?"
      lede="Everyone you invite sees the same projects and spends from the same balance. You can do this later in Settings."
      actions={
        <button type="button" className="btn btn-primary" onClick={onDone}>
          {sent ? "Make your first video" : "Skip — I work alone"}
        </button>
      }
      leave={null}
    >
      <form
        className="flex items-start gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void invite(email).then((ok: boolean) => ok && setEmail(""));
        }}
      >
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="colleague@company.com"
          disabled={busy}
          className="h-[38px] min-w-0 flex-1 rounded-[8px] border border-[var(--edge)] bg-bay-850 px-3 text-[13px] text-ink-100 placeholder:text-ink-400 focus:border-ember-500 focus:outline-none"
        />
        <button type="submit" className="btn btn-secondary shrink-0" disabled={busy || email.trim() === ""}>
          {busy ? "Sending…" : "Send invite"}
        </button>
      </form>

      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {sent ? (
        <p className="mt-2 text-[12px] text-ink-300">{`Invitation sent to ${sent}. Invite someone else, or carry on.`}</p>
      ) : null}
    </Frame>
  );
};

export const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("site");
  const [kit, setKit] = useState<BrandKit>(defaultBrandKit);
  const [website, setWebsite] = useState("");
  const [pulling, setPulling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<Found | null>(null);
  /** What the site gave us, kept apart so "try another address" can discard it. */
  const [read, setRead] = useState<BrandKit | null>(null);

  // Whatever is already saved, so somebody who half-filled this and came back
  // is not starting again.
  useEffect(() => {
    void api
      .settings()
      .then((current) => {
        setKit(current.brandKit);
        if (current.brandKit.website) setWebsite(current.brandKit.website);
      })
      .catch(() => {
        // The defaults are a fine place to start from, and an error banner
        // before anybody has done anything is noise about nothing.
      });
  }, []);

  const pull = async () => {
    const asked = website.trim();
    if (!asked || pulling) return;

    setPulling(true);
    setError(null);
    try {
      const guess = await api.brandFromWebsite(asked);
      setRead({
        ...kit,
        website: guess.website,
        colors: guess.colors,
        // Only what was actually read. Overwriting a face with null would
        // replace a real default with nothing.
        titleFont: guess.titleFont ?? kit.titleFont,
        bodyFont: guess.bodyFont ?? kit.bodyFont,
      });
      setWebsite(guess.website);
      setFound(guess.found);
      setStep("confirm");
      capture("brand_pulled_from_website", { found_colours: guess.found.colours });
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setPulling(false);
    }
  };

  /**
   * Saves, then goes straight to making the first video.
   *
   * Not to the dashboard. Somebody who has just finished setting up has an
   * empty project list, and landing on it means the reward for completing
   * onboarding is a screen that says you have nothing — with the one thing to
   * do next a tile they have to find. The button on the last step already says
   * "start"; this is it starting.
   *
   * Skipping still goes to the dashboard. That person said they did not want
   * to do this now, and answering by opening a different form would be reading
   * "later" as "yes".
   */
  const persist = async (toSave: BrandKit, how: string) => {
    setSaving(true);
    setError(null);
    try {
      await api.saveSettings({ brandKit: { ...toSave, website: website.trim() } });
      capture("brand_kit_completed", { how });
      /**
       * One more question before the first project: who else works here.
       *
       * Here rather than in settings-only because a team is worth the most
       * before there is anything in it — invite somebody now and they see
       * every project from the first, invite them in a month and they arrive
       * to a library they had no part in. It is skippable in one click, which
       * is what stops it being a toll gate.
       */
      setStep("team");
      setSaving(false);
    } catch (cause) {
      setError((cause as Error).message);
      setSaving(false);
    }
  };

  const skip = () => {
    markWelcomeSkipped();
    capture("brand_kit_skipped", { step });
    navigate("/dashboard", { replace: true });
  };

  /* A real way past, on every step. A setup somebody cannot leave is a trap,
     and the kit is editable from settings whenever they get to it. */
  const later = (
    <button type="button" className="btn btn-ghost" onClick={skip} disabled={saving}>
      {"I will do this later"}
    </button>
  );

  const problem = error ? <p className="mt-3 text-[12px] text-signal-bad">{error}</p> : null;

  // ── 1 · the one question ──────────────────────────────────────────────────
  if (step === "site") {
    return (
      <Frame
        step="site"
        title="What is your product called, and where does it live?"
        lede="Give us the address and we will read your colours and type off it, so you are correcting a brand rather than building one. It takes a few seconds."
        leave={later}
        actions={
          <>
            {/* Not a primary button. Setting it by hand is a real route through
                this screen, but it is the longer one, and two filled buttons
                side by side would make somebody choose rather than proceed. */}
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                capture("brand_manual_chosen");
                setStep("kit");
              }}
              disabled={saving}
            >
              {"I will set it up myself"}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void pull()}
              disabled={pulling || website.trim().length === 0}
            >
              {pulling ? "Reading…" : "Read my brand"}
            </button>
          </>
        }
      >
        <label htmlFor="welcome-site" className="block text-[12px] font-medium text-ink-200">
          {"Your product's website"}
        </label>
        <input
          id="welcome-site"
          className="input mt-2 w-full text-[15px]"
          placeholder="acme.com"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          onKeyDown={(event) => {
            // One field and one obvious action: Enter should take it.
            if (event.key === "Enter") {
              event.preventDefault();
              void pull();
            }
          }}
          autoComplete="url"
          autoFocus
          spellCheck={false}
        />
        <p className="mt-2 text-[12px] text-ink-400">
          {"We only read the public page, the way a browser would. Nothing is saved until you say so."}
        </p>
        {problem}
      </Frame>
    );
  }

  // ── 2 · what came back, before anything is kept ───────────────────────────
  if (step === "confirm" && read && found) {
    return (
      <Frame
        step="confirm"
        title="Does this look like you?"
        lede={`${readSummary(found)} It is a guess, so it is worth a look before it becomes the default for every video.`}
        /* Leaving is still an option here. "Try another address" and "Let me
           adjust it" are both ways further in; somebody who decides halfway
           through that today is not the day needs a way out that is not the
           back button. */
        leave={later}
        actions={
          <>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setRead(null);
                setFound(null);
                setError(null);
                setStep("site");
              }}
              disabled={saving}
            >
              {"Try another address"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setKit(read);
                setStep("kit");
              }}
              disabled={saving}
            >
              {"Let me adjust it"}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void persist(read, "from_website")}
              disabled={saving}
            >
              {saving ? "Saving…" : "Make my first video"}
            </button>
          </>
        }
      >
        <div className="rounded-[10px] border border-[var(--edge)] bg-bay-850 p-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Swatch label="Text" colour={read.colors.text} />
            <Swatch label="Background" colour={read.colors.background} />
            <Swatch label="Accent" colour={read.colors.accent} />
            <Swatch label="Second accent" colour={read.colors.accentAlt} />
          </div>

          <div className="mt-4 border-t border-[var(--edge)] pt-4 text-[12px] text-ink-300">
            <span className="text-ink-400">Type: </span>
            <span style={{ fontFamily: brandFontStack(read.titleFont) }}>{read.titleFont}</span>
            {read.bodyFont !== read.titleFont ? (
              <>
                <span className="text-ink-400">{" for titles, "}</span>
                <span style={{ fontFamily: brandFontStack(read.bodyFont) }}>{read.bodyFont}</span>
                <span className="text-ink-400">{" for body"}</span>
              </>
            ) : (
              <span className="text-ink-400">{" throughout"}</span>
            )}
          </div>
        </div>

        {/* A card in the colours themselves. Four hex codes are hard to judge;
            the thing they will actually produce is not. */}
        <div
          className="mt-4 flex aspect-[16/9] flex-col items-center justify-center rounded-[10px] border border-[var(--edge)] px-8 text-center"
          style={{ background: read.colors.background, color: read.colors.text }}
        >
          <p className="text-[26px] font-bold leading-[1.15]" style={{ fontFamily: brandFontStack(read.titleFont) }}>
            {"Connect Discord to your workspace"}
          </p>
          <p className="mt-2 text-[13px] opacity-80" style={{ fontFamily: brandFontStack(read.bodyFont) }}>
            {"A ninety-second walkthrough"}
          </p>
          <span className="mt-4 h-[3px] w-14 rounded-full" style={{ background: read.colors.accent }} />
        </div>

        {problem}
      </Frame>
    );
  }

  // ── 4 · who else works here ───────────────────────────────────────────────
  if (step === "team") {
    return <TeamStep onDone={() => navigate("/projects/new", { replace: true })} />;
  }

  // ── 3 · the kit itself ────────────────────────────────────────────────────
  return (
    <Frame
      step="kit"
      title={read ? "Adjust anything that is not right" : "Set your brand"}
      /* Deliberately not "the default look and voice for every video": the
         panel below says that in almost those words, and the same sentence
         twice on one screen reads as a rendering fault. This says where the
         values came from, which the panel cannot know. */
      lede={
        read
          ? `These came off ${website.replace(/^https?:\/\//, "")}. Nothing is saved until you press the button.`
          : "The colours and type every video will start from. You can come back to this in settings at any time."
      }
      leave={later}
      actions={
        <>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void persist(kit, read ? "adjusted" : "manual")}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save and make my first video"}
          </button>
        </>
      }
    >
      <BrandKitPanel kit={kit} onChange={setKit} />
      {problem}
    </Frame>
  );
};
